import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameData } from '../data/GameData';
import { EndedBy, StageDef, ScriptGenre, FilmographyEntry, TopWorkEntry, AwardHonor } from '../data/types';
import { AWARD_MAP, AwardId } from '../data/tables/Awards';
import { rankSystem } from './RankSystem';
import { saveSystem } from './SaveSystem';
import { achievementSystem } from './AchievementSystem';
import { fanSystem } from './FanSystem';
import { awardSystem } from './AwardSystem';
import { Utils } from '../core/Utils';
import {
    SHAPE_RULES, STAGE_FAIL_COPY, FAIL_TITLE,
    TAIL_NATURAL, TAIL_VOLUNTARY, failAdviceOf, tierOfRank, endedByLabel,
    ShapeContext,
} from '../data/tables/Endings';
import { CareerSummary, CareerStats } from '../data/types';

/**
 * 结局系统（2026-09-29 结局批重写，设计=docs/实现说明.md §3.4g + 迁存章 A5）
 *
 * 三入口（public API 不变）：checkEnding()（满40自动）/ retire()（主动退圈，WeiboPanel 自带确认框）
 * / triggerStageFail(stage)（审查失败，stage 参数自本批起真正使用）。
 * 结束方式三态 endedBy：natural / voluntary / stage_fail。
 * 判定两层（基调×形状；隐藏层 P2 后置未实装）→ 生成 CareerSummary 快照写入 MetaData.lastCareerSummary
 * （结算页只读快照，删档/刷新安全）→ emit GAME_OVER（reason 兼容旧值：natural/voluntary 均 'retired'）。
 * 商店解锁 / 随机池解锁的 meta 写入自 app.ts 收口至此（一处写全，结算页同帧可读）。
 */
export class EndingSystem {
    private static instance: EndingSystem;

    private constructor() { }

    static getInstance(): EndingSystem {
        if (!this.instance) {
            this.instance = new EndingSystem();
        }
        return this.instance;
    }

    /** 检查是否应结束（满40岁） */
    checkEnding(): boolean {
        const state = GameState.getInstance();
        if (state.player.age >= GameConfig.END_AGE) {
            this.endGame('natural'); // 正常到达40岁
            return true;
        }
        return false;
    }

    /** 主动选择退役（月底决策日；UI 侧已有「确认退圈」弹窗，此处直接终局） */
    retire(): boolean {
        this.endGame('voluntary');
        return true;
    }

    /** 阶段审查未通过（stage 自本批起进入结算快照，失败文案按阶段分四段） */
    triggerStageFail(stage: StageDef): void {
        this.endGame('stage_fail', stage.id);
    }

    /** 进行周目结算：补判成就 → 判定 → 快照落 meta → 广播 */
    endGame(endedBy: EndedBy, failStage?: number): void {
        const state = GameState.getInstance();

        // 批 G：本月正好是颁奖月而玩家走不到「确认行程」（阶段失败当月即终局 / 主动退圈）⇒
        // 先把该届的结果与收益补落进 awardHistory，荣誉墙与下面的快照⛔ 少一届（颁奖夜⛔ 补播，马上要切结算页）
        awardSystem.settleDueBeforeGameOver();

        // 结局成就（2026-10-02，设计=docs/实现说明.md §3.4i + 迁存章 A2）：档位/退役方式打成 flag，
        // 走 event 机制随下面的 checkMonthly 补判一并入账并进结算页 newAchievements——
        // 多周目成就收入枯竭的对症解（一局只落一个档位 ⇒ 每个新档位=一次新周目的目标）；
        // 直发点数方案已被用户否决（成就点⛔ 变通关结算工资）。失败局无基调档位，不打档位 flag。
        if (endedBy !== 'stage_fail') {
            state.setFlag(`ACH_END_TIER_${tierOfRank(state.player.rank).id}`);
            if (endedBy === 'voluntary' && state.player.rank >= 6) {
                state.setFlag('ACH_END_VOLUNTARY_HIGH');
            }
        }

        // 补判成就（幂等）：兜住终局才达标的成就（如最后一次滴灌跨过咖位线）
        const newly = achievementSystem.checkMonthly();

        // 本周目全量成就（2026-09-30 用户实测「结算页成就没实装」）：runAchievements = 本局
        // 开局至终局解锁的全部（月度陆续解锁都在内）；旧档缺省 → 兜底退化为补判那一次 newly
        const runAchIds = state.player.runAchievements ?? newly;

        const summary = this.buildSummary(endedBy, failStage, newly, runAchIds);

        // meta 统一写：快照 + 商店解锁 + 随机池解锁（非 stage_fail 且满 40）
        const meta = saveSystem.loadMeta();
        summary.randomPoolUnlockedNow = endedBy !== 'stage_fail'
            && state.player.age >= GameConfig.END_AGE
            && !meta.randomPoolUnlocked;
        meta.lastCareerSummary = summary;
        meta.shopUnlocked = true;
        if (endedBy !== 'stage_fail' && state.player.age >= GameConfig.END_AGE) {
            meta.randomPoolUnlocked = true;
        }
        saveSystem.saveMeta(meta);

        EventBus.emit(GameEvents.STATE_CHANGED);
        // reason 兼容旧值（app.ts 按此停循环/删档/跳结算页；'retired' 涵盖 natural+voluntary）
        EventBus.emit(GameEvents.GAME_OVER,
            endedBy === 'stage_fail' ? 'stage_fail' : 'retired', summary.rank, summary.title);
    }

    /** 生成周目结算快照（结算页唯一数据源） */
    private buildSummary(endedBy: EndedBy, failStage: number | undefined, newly: string[], runAchIds: string[]): CareerSummary {
        const state = GameState.getInstance();
        const p = state.player;
        const rankName = rankSystem.getRankName(p.rank);

        // —— 聚合统计（来源映射见设计文档 §4.2）——
        const filmo = [...(p.filmography ?? [])];
        const works = filmo.length;
        const worksByGenre: Partial<Record<ScriptGenre, number>> = {};
        let scoreSum = 0;
        for (const f of filmo) {
            worksByGenre[f.genre] = (worksByGenre[f.genre] ?? 0) + 1;
            scoreSum += f.score;
        }
        const stats: CareerStats = {
            works,
            worksByGenre,
            courses: Object.values(p.pathCourseCounts ?? {}).reduce((a, b) => a + b, 0),
            posts: p.totalPosts ?? 0,
            likes: p.totalLikes ?? 0,
            knownNpcs: Object.values(p.relationships ?? {}).filter(v => v >= 0).length,
            closeFriends: Object.values(p.relationships ?? {}).filter(v => v >= 80).length,
            totalMoney: p.totalEarnedMoney,
            age: p.age,
        };
        const awards = {
            won: (p.awardHistory ?? []).filter(r => r.playerWon).length,
            nominated: (p.awardHistory ?? []).filter(r => r.playerNominated).length,
        };
        // 获奖陈列快照（2026-10-18）：只收玩家获奖、按获奖年份**从先往后**；角色名一并快照
        //（不从剧本表反查——剧本下架/改名后也不漂）。没有作品记录的空条不摆（旧档/异常）。
        const honors: AwardHonor[] = (p.awardHistory ?? [])
            .filter(r => r.playerWon)
            .slice()
            .sort((a, b) => a.year - b.year)
            .map(r => {
                const def = AWARD_MAP[r.awardId as AwardId];
                const win = r.nominees?.[0];
                const script = win?.scriptId ? GameData.SCRIPT_MAP[win.scriptId] : undefined;
                return {
                    awardId: r.awardId,
                    awardName: def?.name ?? r.awardId,
                    categoryName: def?.categoryNames[r.category] ?? r.category,
                    edition: r.edition,
                    workName: win?.workName ?? '',
                    // 固定阵容（roleIndex = -1）没有 roles 位，角色名读提名快照（2026-10-21）
                    roleName: win?.roleName
                        || (script && win && win.roleIndex >= 0 ? script.roles[win.roleIndex]?.name : undefined),
                    year: r.year,
                };
            })
            .filter(h => h.workName);

        // —— 判定：失败结局独立成路；正常结局走 基调 × 形状 ——
        let tierId: string;
        let shapeId: string | undefined;
        let title: string;
        let summaryText: string;
        let adviceText: string | undefined;

        if (endedBy === 'stage_fail') {
            tierId = 'FAIL';
            title = FAIL_TITLE;
            const stageIdx = Math.min(Math.max(failStage ?? 1, 1), STAGE_FAIL_COPY.length) - 1;
            summaryText = STAGE_FAIL_COPY[stageIdx].replace('%works%', String(works));
            const stage = GameData.getStageById(failStage ?? 1);
            adviceText = failAdviceOf(stage ? Math.max(1, stage.requiredRank - p.rank) : 3);
        } else {
            const tier = tierOfRank(p.rank);
            tierId = tier.id;
            const ctx: ShapeContext = {
                won: awards.won,
                works,
                movieShare: works > 0 ? (worksByGenre['movie'] ?? 0) / works : 0,
                avgScore: works > 0 ? scoreSum / works : 0,
                fansPerFame: p.fans / Math.max(1, p.fame),
                totalShots: p.totalShotsCompleted,
                allShort: works > 0 && filmo.every(f => f.genre === 'short'),
            };
            const shape = SHAPE_RULES.find(r => r.test(ctx));
            shapeId = shape?.id;
            title = shape ? `${shape.prefix}${tier.title}` : tier.title;
            const tail = endedBy === 'natural' ? TAIL_NATURAL : TAIL_VOLUNTARY;
            summaryText = this.fillTemplate(tier.template, {
                rankName, stats, awards, fans: p.fans,
            }) + tail;
        }

        // —— 新解锁成就（checkMonthly 幂等补判的结果）——
        // 本周目全量（runAchievements 优先；旧档缺省退化补判 newly）
        const newAchievements = (runAchIds ?? newly)
            .map(id => GameData.ACHIEVEMENT_MAP[id])
            .filter(Boolean)
            .map(a => ({ id: a.id, name: a.name, points: a.points }));

        return {
            endedBy,
            failStage,
            rank: p.rank,
            rankName,
            fame: p.fame,
            fans: p.fans,
            reputation: p.attributes.reputation,
            tierId,
            shapeId,
            title,
            summaryText,
            adviceText,
            stats,
            awards,
            honors,
            newAchievements,
            earnedPoints: newAchievements.reduce((s, a) => s + a.points, 0),
            randomPoolUnlockedNow: false, // endGame 内依据先前 meta 回填
            // 生涯代表作前三（2026-09-30 二次拍板）：代表作系数降序取 3，结算时定格
            topWorks: this.pickTopWorks(filmo),
            filmography: filmo,
            finishedAt: Date.now(),
        };
    }

    /**
     * 代表作系数 = ( min(热度,100)÷10 + 评分 ) ÷ 2 × 番位系数（现行 FAME 表，无番位 0.5）。
     * 热度归一到 0~10 与评分（2.1~9.9）同权——0.5/0.5 直加会让热度（16~150+）碾压评分。
     * 降序取 3（展示强化 2026-09-30：条目同时定格角色名与热度，结算页领奖台展示用，
     * 之后表回改 ⛔ 回写）；系数同分保先入履历（引擎稳定排序）；剧本下架的作品不参与评选。
     */
    private pickTopWorks(filmo: FilmographyEntry[]): TopWorkEntry[] {
        const coefOf = (f: FilmographyEntry): { entry: TopWorkEntry; coef: number } | undefined => {
            const script = GameData.SCRIPT_MAP[f.scriptId];
            const role = script?.roles[f.roleIndex];
            if (!role) return undefined; // 剧本/角色已下架 → 不参与评选（履历节仍展示）
            const factor = role.billing === undefined
                ? GameConfig.FAME_FACTOR_NO_BILLING
                : GameConfig.FAME_BILLING_FACTORS[Math.min(role.billing, 5)];
            const heat = fanSystem.workHeatOf(script);
            return {
                entry: { ...f, roleName: role.name, heat },
                coef: ((Math.min(heat, 100) / 10 + f.score) / 2) * factor,
            };
        };
        return filmo
            .map(coefOf)
            .filter((x): x is { entry: TopWorkEntry; coef: number } => x !== undefined)
            .sort((a, b) => b.coef - a.coef)
            .slice(0, 3)
            .map(x => x.entry);
    }

    /** 模板填空：%age% %works% %awards%（短语坑） %fans%（新浪口径格式化） %rank% */
    private fillTemplate(
        template: string,
        v: { rankName: string; stats: CareerStats; awards: { won: number }; fans: number },
    ): string {
        const awardsPhrase = v.awards.won > 0 ? `${v.awards.won} 座奖杯` : '一段段被记住的表演';
        return template
            .replace('%age%', String(v.stats.age))
            .replace('%works%', String(v.stats.works))
            .replace('%awards%', awardsPhrase)
            .replace('%fans%', Utils.formatCountCN(v.fans))
            .replace('%rank%', v.rankName);
    }

    /** 获取最终咖位（供结算界面展示兜底用） */
    getFinalRank(): number {
        return GameState.getInstance().player.rank;
    }
}

/** 导出单例 */
export const endingSystem = EndingSystem.getInstance();
